import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronRight, Circle, Square, SquareCheck } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/utils'
import { Text } from './text'
import { FollowWindowBreakpoint } from './use-responsive'

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const dropdownMenuSubTriggerVariants = cva(
  'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
  {
    variants: {
      variant: {
        default: 'focus:bg-sub-surface-bg data-[state=open]:bg-sub-surface-bg',
        inverse: 'text-header-inverse focus:bg-sub-surface-bg-inverse data-[state=open]:bg-sub-surface-bg-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuContentVariants = cva(
  [
    'z-20 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg',
    'origin-top-left',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
    'data-[side=bottom]:slide-in-from-top-0',
    'data-[state=closed]:duration-200 data-[state=open]:duration-300',
  ],
  {
    variants: {
      variant: {
        default: 'border-border bg-background text-header',
        inverse: 'border-border-inverse bg-background-inverse text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuSubContentVariants = cva(
  'z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  {
    variants: {
      variant: {
        default: 'border-border bg-background text-header',
        inverse: 'border-border-inverse bg-background-inverse text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuItemVariants = cva(
  'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        default: 'focus:bg-sub-surface-bg focus:text-header',
        inverse: 'text-header-inverse focus:bg-sub-surface-bg-inverse focus:text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuCheckboxItemVariants = cva(
  'flex min-w-64 cursor-pointer select-none items-center justify-start gap-2 rounded-sm py-2 pl-6 font-semibold text-base outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        default: 'focus:bg-sub-surface-bg focus:text-header',
        inverse: 'text-header-inverse focus:bg-sub-surface-bg-inverse focus:text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuRadioItemVariants = cva(
  'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        default: 'focus:bg-sub-surface-bg focus:text-header',
        inverse: 'text-header-inverse focus:bg-sub-surface-bg-inverse focus:text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dropdownMenuLabelVariants = cva('px-2 py-1.5 text-sm font-semibold', {
  variants: {
    variant: {
      default: 'text-header',
      inverse: 'text-header-inverse',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const dropdownMenuSeparatorVariants = cva('-mx-1 my-1 h-px', {
  variants: {
    variant: {
      default: 'bg-sub-surface-bg',
      inverse: 'bg-border-inverse',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> &
    VariantProps<typeof dropdownMenuSubTriggerVariants> & {
      inset?: boolean
    }
>(({ className, inset, variant, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(dropdownMenuSubTriggerVariants({ variant }), inset && 'pl-8', className)}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto h-4 w-4" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> &
    VariantProps<typeof dropdownMenuSubContentVariants>
>(({ className, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(dropdownMenuSubContentVariants({ variant }), className)}
    {...props}
  />
))
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

type DropdownMenuContentProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> &
  VariantProps<typeof dropdownMenuContentVariants> & {
    /** Class on the portal wrapper (e.g. `select-app-mfe` when body is not already scoped). Storybook preview adds `select-app-mfe` to body. */
    wrapperClassName?: string
  }

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 15, wrapperClassName, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    {/* DropdownMenu portals to document.body; wrapperClassName scopes prefixed CSS when needed */}
    <div className={wrapperClassName}>
      <FollowWindowBreakpoint>
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          forceMount
          className={cn(dropdownMenuContentVariants({ variant }), className)}
          {...props}
        />
      </FollowWindowBreakpoint>
    </div>
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> &
    VariantProps<typeof dropdownMenuItemVariants> & {
      inset?: boolean
    }
>(({ className, inset, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(dropdownMenuItemVariants({ variant }), inset && 'pl-8', className)}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> &
    VariantProps<typeof dropdownMenuCheckboxItemVariants>
>(({ className, children, checked, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(dropdownMenuCheckboxItemVariants({ variant }), className)}
    checked={checked}
    {...props}
  >
    {!checked && <Square className="h-6 w-6" />}
    <DropdownMenuPrimitive.ItemIndicator>
      <SquareCheck className="h-6 w-6 text-primary-accent" />
    </DropdownMenuPrimitive.ItemIndicator>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

type DropdownMenuRadioItemProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> &
  VariantProps<typeof dropdownMenuRadioItemVariants>

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(({ className, children, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(dropdownMenuRadioItemVariants({ variant }), className)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> &
    VariantProps<typeof dropdownMenuLabelVariants> & {
      inset?: boolean
    }
>(({ className, inset, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(dropdownMenuLabelVariants({ variant }), inset && 'pl-8', className)}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> &
    VariantProps<typeof dropdownMenuSeparatorVariants>
>(({ className, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(dropdownMenuSeparatorVariants({ variant }), className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

// Collapsible Menu Item Component
const DropdownMenuCollapsibleItem = React.forwardRef<
  HTMLDivElement,
  {
    label: string
    icon?: React.ReactNode
    children?: React.ReactNode
    onItemClick?: () => void
    className?: string
    chevronClassName?: string
    contentClassName?: string
  }
>(({ label, icon, children, onItemClick, className, chevronClassName, contentClassName }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const handleClick = (e: React.MouseEvent) => {
    if (children) {
      e.preventDefault()
      e.stopPropagation()
      toggleOpen()
    } else {
      onItemClick?.()
    }
  }

  return (
    <div ref={ref}>
      <DropdownMenuItem
        className={cn('p-0', className)}
        onSelect={e => {
          if (children) {
            e.preventDefault()
          }
        }}
      >
        <button
          type="button"
          className="flex items-center justify-between w-full px-8 py-7 text-sm bg-transparent border-none cursor-pointer"
          onClick={handleClick}
        >
          <div className="flex items-center gap-6">
            {icon}
            <Text variant="base-sb">{label}</Text>
          </div>
          {children && (
            <ChevronRight
              className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-90', chevronClassName)}
            />
          )}
        </button>
      </DropdownMenuItem>

      {/* Submenu items */}
      {children && (
        <div
          className={cn(
            'overflow-hidden transition-all duration-200',
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
            contentClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  )
})
DropdownMenuCollapsibleItem.displayName = 'DropdownMenuCollapsibleItem'

// Submenu Item Component
const DropdownMenuSubItem = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode
    onClick?: () => void
    className?: string
  }
>(({ children, onClick, className }, ref) => (
  <div ref={ref}>
    <DropdownMenuItem
      onClick={onClick}
      className={cn('p-0 border border-t-0 border-r-0 border-l-0 border-b-1 border-border rounded-none', className)}
    >
      {children}
    </DropdownMenuItem>
  </div>
))
DropdownMenuSubItem.displayName = 'DropdownMenuSubItem'

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuCollapsibleItem,
  DropdownMenuContent,
  type DropdownMenuContentProps,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  type DropdownMenuRadioItemProps,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubItem,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
}
